import { z } from 'zod';
import { PaginationMetaSchema } from './pagination.js';

/**
 * Silvicultural works & expenses ("Lucrări"). One record = one planned (PLAN)
 * or executed (FAPT) work on a parcel/sub-parcel, carrying its cost breakdown.
 * See WORKS_EXPENSES_MODULE.md for the source-column mapping.
 */

export const PlanFaptSchema = z.enum(['PLAN', 'FAPT']);
export type PlanFapt = z.infer<typeof PlanFaptSchema>;

/** Minimal parcel brief attached to a work when the UCF resolved to a parcel. */
export const WorkParcelBriefSchema = z.object({
  id: z.string().uuid(),
  ucf: z.string().nullable(),
  projectUcfKey: z.string().nullable(),
  raion: z.string().nullable(),
});
export type WorkParcelBrief = z.infer<typeof WorkParcelBriefSchema>;

export const WorkRecordSchema = z.object({
  id: z.string().uuid(),
  year: z.number().int(),
  month: z.number().int().nullable(),
  quarter: z.number().int().nullable(),
  planFapt: PlanFaptSchema,

  entitateSilvica: z.string().nullable(),
  entityUp: z.string().nullable(),
  ocolSilvic: z.string().nullable(),
  raion: z.string().nullable(),
  primarieComposite: z.string().nullable(),
  codPrimarie: z.string().nullable(),
  parcela: z.string().nullable(),
  subparcela: z.string().nullable(),
  parcelaPlus: z.string().nullable(),
  ucf: z.string().nullable(),
  ucfPlus: z.string().nullable(),
  parcelId: z.string().uuid().nullable(),
  parcel: WorkParcelBriefSchema.nullable().optional(),

  workGroup: z.number().int().nullable(),
  workType: z.string().nullable(),
  fundingSource: z.string().nullable(),
  landOwner: z.string().nullable(),
  personnelNumber: z.string().nullable(),
  reported: z.number().int().nullable(),
  plannedAreaHa: z.number().nullable(),
  totalAreaHa: z.number().nullable(),
  placementScheme: z.string().nullable(),
  baseSpecies: z.string().nullable(),
  baseSeedlings: z.number().int().nullable(),
  mixSeedlings: z.number().int().nullable(),
  shrubSeedlings: z.number().int().nullable(),
  totalSeedlings: z.number().int().nullable(),

  wages: z.number().nullable(),
  contributions: z.number().nullable(),
  fuel: z.number().nullable(),
  services: z.number().nullable(),
  otherCosts: z.number().nullable(),
  plantingMaterialSum: z.number().nullable(),
  totalDirect: z.number().nullable(),
  overhead1: z.number().nullable(),
  overhead2: z.number().nullable(),
  otherFinal: z.number().nullable(),
  totalFinal: z.number().nullable(),
  totalReported: z.number().nullable(),

  pdfLink: z.string().nullable(),
  sasdKey: z.string().nullable(),
  isArchived: z.boolean(),
  archivedAt: z.string().nullable(),
  createdAt: z.string(),
  updatedAt: z.string(),
  deletedAt: z.string().nullable(),
});
export type WorkRecordDto = z.infer<typeof WorkRecordSchema>;

export const WorkSortFieldSchema = z.enum([
  'year',
  'month',
  'quarter',
  'planFapt',
  'entitateSilvica',
  'ocolSilvic',
  'raion',
  'primarieComposite',
  'workType',
  'totalFinal',
  'totalDirect',
  'wages',
  'createdAt',
]);
export type WorkSortField = z.infer<typeof WorkSortFieldSchema>;

export const WorkRecordListQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(500).default(50),
  search: z.string().optional(),
  // CSV multi-select filters (empty slot = include NULL, matching the
  // parcels/photos convention).
  entitatiSilvice: z.string().optional(),
  ocoaleSilvice: z.string().optional(),
  raioane: z.string().optional(),
  years: z.string().optional(),
  months: z.string().optional(),
  quarters: z.string().optional(),
  workTypes: z.string().optional(),
  fundingSources: z.string().optional(),
  planFapt: PlanFaptSchema.optional(),
  parcelId: z.string().uuid().optional(),
  includeArchived: z.coerce.boolean().optional().default(false),
  includeDeleted: z.coerce.boolean().optional().default(false),
  sort: WorkSortFieldSchema.default('year'),
  order: z.enum(['asc', 'desc']).default('desc'),
});
export type WorkRecordListQuery = z.infer<typeof WorkRecordListQuerySchema>;

export const WorkRecordTotalsSchema = z.object({
  count: z.number().int(),
  totalFinal: z.number(),
  totalDirect: z.number(),
  wages: z.number(),
  distinctParcels: z.number().int(),
});

export const WorkRecordListResponseSchema = z.object({
  items: z.array(WorkRecordSchema),
  pagination: PaginationMetaSchema,
  totals: WorkRecordTotalsSchema,
});
export type WorkRecordListResponse = z.infer<typeof WorkRecordListResponseSchema>;

export const WorkRecordFacetsResponseSchema = z.object({
  entitatiSilvice: z.array(z.string()),
  ocoaleSilvice: z.array(z.string()),
  raioane: z.array(z.string()),
  years: z.array(z.number().int()),
  workTypes: z.array(z.string()),
  fundingSources: z.array(z.string()),
  hasNullEntitateSilvica: z.boolean(),
  hasNullOcolSilvic: z.boolean(),
  hasNullRaion: z.boolean(),
});
export type WorkRecordFacetsResponse = z.infer<typeof WorkRecordFacetsResponseSchema>;

// ---------------------------------------------------------------------------
// Create / update (data-entry form, mirrors the `fp` sheet)
// ---------------------------------------------------------------------------

const optNum = z.number().nullable().optional();
const optInt = z.number().int().nullable().optional();
const optStr = (max: number) => z.string().max(max).nullable().optional();

export const WorkRecordCreateInputSchema = z
  .object({
    year: z.number().int().min(1900).max(2200),
    month: z.number().int().min(1).max(12).nullable().optional(),
    quarter: z.number().int().min(1).max(4).nullable().optional(),
    planFapt: PlanFaptSchema,
    entitateSilvica: optStr(100),
    entityUp: optStr(50),
    ocolSilvic: optStr(100),
    raion: optStr(100),
    primarieComposite: optStr(200),
    codPrimarie: optStr(20),
    parcela: optStr(50),
    subparcela: optStr(20),
    parcelaPlus: optStr(50),
    ucf: optStr(50),
    ucfPlus: optStr(60),
    parcelId: z.string().uuid().nullable().optional(),
    workGroup: optInt,
    workType: optStr(300),
    fundingSource: optStr(120),
    landOwner: optStr(60),
    personnelNumber: optStr(40),
    reported: optInt,
    plannedAreaHa: optNum,
    totalAreaHa: optNum,
    placementScheme: optStr(60),
    baseSpecies: optStr(60),
    baseSeedlings: optInt,
    mixSeedlings: optInt,
    shrubSeedlings: optInt,
    wages: optNum,
    fuel: optNum,
    services: optNum,
    otherCosts: optNum,
    plantingMaterialSum: optNum,
    pdfLink: optStr(1000),
  })
  .strict();
export type WorkRecordCreateInput = z.infer<typeof WorkRecordCreateInputSchema>;

export const WorkRecordUpdateInputSchema = WorkRecordCreateInputSchema.partial()
  .strict()
  .refine((v) => Object.keys(v).length > 0, { message: 'At least one field required' });
export type WorkRecordUpdateInput = z.infer<typeof WorkRecordUpdateInputSchema>;

// ---------------------------------------------------------------------------
// Import preview row (returned by the works /parse route)
// ---------------------------------------------------------------------------

export const ImportWorkParsedRowSchema = z.object({
  row: z.number().int(),
  source: z.enum(['baza', 'arhiv']),
  isArchived: z.boolean(),
  year: z.number().int().nullable(),
  planFapt: PlanFaptSchema.nullable(),
  entitateSilvica: z.string().nullable(),
  ocolSilvic: z.string().nullable(),
  primarieComposite: z.string().nullable(),
  ucfPlus: z.string().nullable(),
  workType: z.string().nullable(),
  totalFinal: z.number().nullable(),
  dedupeKey: z.string(),
  valid: z.boolean(),
});
export type ImportWorkParsedRow = z.infer<typeof ImportWorkParsedRowSchema>;

// ---------------------------------------------------------------------------
// Reports
// ---------------------------------------------------------------------------

export const WorkArchiveYearInputSchema = z
  .object({ year: z.number().int().min(1900).max(2200) })
  .strict();
export type WorkArchiveYearInput = z.infer<typeof WorkArchiveYearInputSchema>;

/** Plan/Fapt report: one row per work item with the cost breakdown. */
export const WorkPlanFaptReportRowSchema = z.object({
  quarter: z.number().int().nullable(),
  month: z.number().int().nullable(),
  primarieComposite: z.string().nullable(),
  planFapt: PlanFaptSchema,
  parcelaPlus: z.string().nullable(),
  ocolSilvic: z.string().nullable(),
  workType: z.string().nullable(),
  wages: z.number(),
  contributions: z.number(),
  fuel: z.number(),
  services: z.number(),
  materials: z.number(),
  plantingMaterialSum: z.number(),
  totalFinal: z.number(),
});
export const WorkPlanFaptReportResponseSchema = z.object({
  rows: z.array(WorkPlanFaptReportRowSchema),
  totals: z.object({
    wages: z.number(),
    contributions: z.number(),
    fuel: z.number(),
    services: z.number(),
    plantingMaterialSum: z.number(),
    totalFinal: z.number(),
    count: z.number().int(),
  }),
});
export type WorkPlanFaptReportResponse = z.infer<typeof WorkPlanFaptReportResponseSchema>;

/** Anexa 7/8: cost sums grouped by work category, plan vs fapt with % fulfilment. */
export const WorkAnexa78RowSchema = z.object({
  workType: z.string(),
  planTotal: z.number(),
  faptTotal: z.number(),
  fulfilmentPct: z.number(),
});
export const WorkAnexa78ResponseSchema = z.object({
  year: z.number().int(),
  rows: z.array(WorkAnexa78RowSchema),
  grandPlan: z.number(),
  grandFapt: z.number(),
});
export type WorkAnexa78Response = z.infer<typeof WorkAnexa78ResponseSchema>;

/** Anexa 1: annual summary by forest district (UP), plan vs actual. */
export const WorkAnexa1RowSchema = z.object({
  ocolSilvic: z.string(),
  planArea: z.number(),
  faptArea: z.number(),
  planCost: z.number(),
  faptCost: z.number(),
  count: z.number().int(),
});
export const WorkAnexa1ResponseSchema = z.object({
  year: z.number().int(),
  rows: z.array(WorkAnexa1RowSchema),
  totals: z.object({
    planArea: z.number(),
    faptArea: z.number(),
    planCost: z.number(),
    faptCost: z.number(),
    count: z.number().int(),
  }),
});
export type WorkAnexa1Response = z.infer<typeof WorkAnexa1ResponseSchema>;

export const WorkReportQuerySchema = z.object({
  year: z.coerce.number().int().min(1900).max(2200).optional(),
  entitateSilvica: z.string().optional(),
  ocolSilvic: z.string().optional(),
  quarter: z.coerce.number().int().min(1).max(4).optional(),
  planFapt: PlanFaptSchema.optional(),
  includeArchived: z.coerce.boolean().optional().default(false),
});
export type WorkReportQuery = z.infer<typeof WorkReportQuerySchema>;
